import React, { useState, useEffect } from "react";
import {
    Box,
    Grid,
    TextField,
    Button,
    CircularProgress,
    Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import configServ from "../../services/config";
import toast from "react-hot-toast";

const CompensationDetails = ({ employeeId, companyId }) => {
    // 1. STATE FOR FORM FIELDS
    const [formData, setFormData] = useState({
        basic_salary: "",
        hra: "",
        annual_leave: "",
        total_leave: "",
    });

    const [loading, setLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);

    // 2. FETCH EXISTING COMPENSATION DATA (FOR UPDATE MODE)
    useEffect(() => {
        const fetchCompensationDetails = async () => {
            if (!employeeId) return; // Agar employeeId nahi hai matlab 'Create Mode' hai

            try {
                setLoading(true);
                const response = await configServ.getCompensationDetails({ employee_id: employeeId });

                if (response.status && response.data) {
                    setFormData({
                        basic_salary: response.data.basic_salary || "",
                        hra: response.data.hra || "",
                        annual_leave: response.data.annual_leave || "",
                        total_leave: response.data.total_leave || "",
                    });
                }
            } catch (error) {
                console.error("Error fetching compensation details:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchCompensationDetails();
    }, [employeeId]);

    // 3. HANDLE INPUT CHANGES
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    // Total CTC auto-calculate (Basic + HRA) — display only
    const totalCTC = (parseFloat(formData.basic_salary) || 0) + (parseFloat(formData.hra) || 0);

    // 4. SUBMIT/SAVE DATA (CREATE OR UPDATE API)
    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setSubmitLoading(true);

            const payload = {
                ...formData,
                employee_id: employeeId,
                company_id: companyId,
            };

            const result = await configServ.saveCompensationDetails(payload);

            if (result.status) {
                toast.success("Compensation details saved successfully!");
            } else {
                toast.error(result.message || "Something went wrong");
            }
        } catch (error) {
            console.error("Error saving compensation details:", error);
            toast.error("Failed to save record.");
        } finally {
            setSubmitLoading(false);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="250px">
                <CircularProgress size={40} />
            </Box>
        );
    }

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <Grid container spacing={2.5}>
                {/* Salary Row */}
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        type="number"
                        name="basic_salary"
                        value={formData.basic_salary}
                        onChange={handleChange}
                        label="Basic Salary"
                        placeholder="e.g. 25000"
                        variant="outlined"
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        type="number"
                        name="hra"
                        value={formData.hra}
                        onChange={handleChange}
                        label="HRA"
                        placeholder="e.g. 5000"
                        variant="outlined"
                    />
                </Grid>

                {/* Leave Row */}
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        type="number"
                        name="annual_leave"
                        value={formData.annual_leave}
                        onChange={handleChange}
                        label="Annual Leave"
                        placeholder="e.g. 12"
                        variant="outlined"
                    />
                </Grid>
                {/*<Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        type="number"
                        name="total_leave"
                        value={formData.total_leave}
                        onChange={handleChange}
                        label="Total Leave"
                        placeholder="e.g. 24"
                        variant="outlined"
                    />
                </Grid>*/}

                {/* Auto-calculated Total CTC (read-only display) */}
                <Grid item xs={12}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#0f172a" }}>
                        Total CTC (Basic + HRA): ₹ {totalCTC.toFixed(2)}
                    </Typography>
                </Grid>
            </Grid>

            {/* FORM ACTION CONTROL */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
                <Button
                    type="submit"
                    variant="contained"
                    disabled={submitLoading}
                    startIcon={submitLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                    sx={{
                        borderRadius: "10px",
                        px: 4,
                        py: 1.2,
                        fontWeight: "600",
                        textTransform: "none",
                    }}
                >
                    {submitLoading ? "Saving..." : "Save Details"}
                </Button>
            </Box>
        </Box>
    );
};

export default CompensationDetails;